/**
 * @fileoverview Molecule: the wordmark, and what Testo mode does to it.
 * @module components/molecules/BrandTitle
 *
 * @description
 * In Peak mode the wordmark is simply set in the peak face. In Testo mode it
 * is *defaced*: "Peak" is struck out and "TESTO" is scrawled over it, bigger,
 * with a "new!" burst — the visual joke the whole app is built around.
 *
 * The decoration is entirely `aria-hidden`; the accessible name comes from a
 * single visually hidden label, so a screen reader hears "Testo Maxxing" and
 * not "Testo Peak Maxxing new".
 */

import { MODE_LABEL } from "@/lib/domain"
import { AppMode } from "@/types/types"

import "./BrandTitle.css"

/** Props accepted by {@link BrandTitle}. */
export interface BrandTitleProps {
	/** The active mode. */
	readonly mode: AppMode
}

/**
 * The application wordmark.
 *
 * @param props - The active mode.
 * @returns The rendered wordmark.
 *
 * @example
 * <BrandTitle mode={AppMode.TESTO} />
 */
export function BrandTitle({ mode }: BrandTitleProps) {
	const isTesto = mode === AppMode.TESTO

	return (
		<p className={`brand brand--${mode}`}>
			<span className="visually-hidden">{MODE_LABEL[mode]}</span>

			<span className="brand__art" aria-hidden="true">
				<span className="brand__stack">
					<span
						className={
							isTesto
								? "brand__peak brand__peak--struck"
								: "brand__peak"
						}
					>
						Peak
					</span>
					{isTesto && <span className="brand__testo">Testo</span>}
				</span>{" "}
				<span className="brand__maxxing">Maxxing</span>
				{isTesto && <span className="brand__new">new!</span>}
			</span>
		</p>
	)
}
